import * as React from "react";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { HistoryRegular } from "@fluentui/react-icons";
import { officeEditingService, type OfficeEditorConfig } from "../../services/officeEditingService";
import { loadOfficeApi, DocsApiEditor } from "./officeApiLoader";
import { OfficeOpenDialog } from './OfficeOpenDialog';
import { OfficeWorkspaceStatus } from './OfficeWorkspaceStatus';
import { speEditingService, type WorkspaceStatus } from '../../services/speEditingService';
import { documentService } from '../../services/documentService';
import { DocumentVersionsDialog } from "./DocumentVersionsDialog";

/**
 * Word-, Excel- und PowerPoint-Dateien im Browser (ONLYOFFICE Document Server).
 *
 * Das Gegenstück zu `PdfDocumentView`, und bewusst nach demselben Muster: eine
 * schmale Leiste mit den Fassungen, darunter die volle Fläche für den Editor.
 *
 * Der Unterschied zum PDF-Weg: hier lädt nichts in den Browser. Der Document
 * Server holt sich die Datei selbst beim Backend und schreibt sie selbst zurück
 * — diese Komponente stellt nur die Fläche bereit und reicht die serverseitig
 * signierte Konfiguration durch. Was der Nutzer darf, ist darin schon
 * entschieden; es gibt hier nichts zu prüfen und nichts zu ergänzen.
 */

const useStyles = makeStyles({
    root: { height: "100%", display: "flex", flexDirection: "column" },
    bar: {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "6px",
        padding: "6px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    hinweis: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
    body: { flex: 1, minHeight: 0, position: "relative" },
    center: { padding: "24px", color: tokens.colorNeutralForeground3 },
});

const OnlyOfficeDocumentView: React.FC<{
    /** KMVS-Dokument. Fehlt es, kommt die Konfiguration über `configLaden`. */
    docId?: number;
    fileName: string;
    /**
     * Quelle ohne Dokument-Id (Kundenakten-Ablage, Plan 01.10.2026, B2): liefert
     * die serverseitig signierte Nur-lesen-Konfiguration. Fassungen gibt es dann
     * nicht — die Datei ist kein Dokument.
     */
    configLaden?: () => Promise<OfficeEditorConfig>;
    /** Stabiler Bezeichner für den Platzhalter, wenn es keine Dokument-Id gibt. */
    kennung?: string;
}> = ({ docId, fileName, configLaden, kennung }) => {
    const styles = useStyles();
    const quelle = kennung ?? `doc-${docId ?? "x"}`;
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const editorRef = React.useRef<DocsApiEditor | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [bereit, setBereit] = React.useState(false);
    const [schreibbar, setSchreibbar] = React.useState(false);
    const [versionsOpen, setVersionsOpen] = React.useState(false);
    // Hochzählen baut den Editor neu auf — nötig nach dem Wiederherstellen einer
    // früheren Fassung, denn dann zeigt das Dokument auf eine andere Datei.
    const [neuAufbauen, setNeuAufbauen] = React.useState(0);

    // Jede Instanz braucht ihre eigene Ziel-Id: ONLYOFFICE ersetzt das Element
    // mit dieser Id durch sein iframe. Zwei gleichzeitig offene Dokumente mit
    // derselben Id würden einander überschreiben.
    const platzhalterId = React.useMemo(
        () => `onlyoffice-${quelle}-${Math.random().toString(36).slice(2, 8)}`,
        [quelle],
    );

    React.useEffect(() => {
        let aktiv = true;
        setFehler(null);
        setBereit(false);

        (async () => {
            try {
                const laden = configLaden ?? (() => officeEditingService.config(docId as number));
                const { documentServerUrl, config, editable } = await laden();
                if (!aktiv) return;
                setSchreibbar(editable);

                const api = await loadOfficeApi(documentServerUrl);
                if (!aktiv) return;

                // Der Platzhalter muss beim Aufruf im DOM stehen — React hat ihn
                // gerendert, sobald dieser Effekt läuft.
                if (!containerRef.current) return;

                editorRef.current = new api.DocEditor(platzhalterId, {
                    ...config,
                    // Nur diese drei Angaben kommen vom Browser. Sie sind nicht
                    // Teil der Signatur und erreichen den Document Server nicht —
                    // `events` wertet allein das SDK im Browser aus.
                    width: "100%",
                    height: "100%",
                    events: {
                        onAppReady: () => aktiv && setBereit(true),
                        onError: (event: { data?: { errorDescription?: string } }) => {
                            if (!aktiv) return;
                            setFehler(
                                event?.data?.errorDescription ||
                                    "Der Office-Editor meldet einen Fehler.",
                            );
                        },
                    },
                });
            } catch (err) {
                if (aktiv) setFehler((err as Error).message);
            }
        })();

        return () => {
            aktiv = false;
            // Ohne dieses Aufräumen bliebe die Bearbeitungssitzung im Document
            // Server offen: er hielte das Dokument weiter für „in Bearbeitung"
            // und schriebe erst nach seinem eigenen Zeitablauf zurück.
            try {
                editorRef.current?.destroyEditor();
            } catch {
                // Der Editor war nie fertig aufgebaut — dann gibt es nichts abzubauen.
            }
            editorRef.current = null;
        };
        // `configLaden` ist je Aufruf eine neue Closure; die Identität der Datei
        // hängt an `quelle`/`platzhalterId`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId, platzhalterId, neuAufbauen]);

    return (
        <div className={styles.root}>
            <div className={styles.bar}>
                <span className={styles.hinweis}>
                    {fehler
                        ? ""
                        : schreibbar
                          ? "Änderungen werden als neue Fassung gespeichert."
                          : "Leseansicht — diese Datei wird nicht zurückgeschrieben."}
                </span>
                {docId != null && (
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<HistoryRegular />}
                        onClick={() => setVersionsOpen(true)}
                        title={`Frühere Fassungen von ${fileName}`}
                    >
                        Fassungen
                    </Button>
                )}
            </div>

            <div className={styles.body}>
                {fehler && <div className={styles.center}>{fehler}</div>}
                {!fehler && !bereit && <div className={styles.center}>Editor wird geladen…</div>}
                {/* Bleibt auch im Lade- und Fehlerfall im DOM: ONLYOFFICE braucht
                    das Element mit dieser Id, sobald der Aufbau beginnt. */}
                <div ref={containerRef} style={{ height: "100%", display: fehler ? "none" : undefined }}>
                    <div id={platzhalterId} />
                </div>
            </div>

            {docId != null && (
                <DocumentVersionsDialog
                    docId={docId}
                    open={versionsOpen}
                    onOpenChange={setVersionsOpen}
                    onRestored={() => setNeuAufbauen((k) => k + 1)}
                    canRestore={schreibbar}
                />
            )}
        </div>
    );
};


/** Providerprüfung erfolgt vor dem Editoraufbau, Recovery bleibt unabhängig von Startfreigaben. */
export const OfficeDocumentView: React.FC<
    React.ComponentProps<typeof OnlyOfficeDocumentView> & {
        /** Im KMVS-Viewer direkt Web/Desktop wählen; die separate Editorroute bleibt unverändert. */
        chooseTargetOnOpen?: boolean;
    }
> = (props) => {
    const [onlyOfficeChosen, setOnlyOfficeChosen] = React.useState(() => {
        try {
            const chosen =
                props.docId != null &&
                sessionStorage.getItem("kmvs-office-web-choice") ===
                    String(props.docId);
            if (chosen) sessionStorage.removeItem("kmvs-office-web-choice");
            return (
                chosen ||
                new URLSearchParams(window.location.search).get("office") ===
                    "web"
            );
        } catch {
            return false;
        }
    });
    const [mode, setMode] = React.useState<
        "loading" | "onlyoffice" | "spe" | "readonly" | "disabled"
    >("loading");
    const [workspace, setWorkspace] = React.useState<WorkspaceStatus | null>(
        null,
    );
    const [dialog, setDialog] = React.useState(false);
    const [versions, setVersions] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    React.useEffect(() => {
        let alive = true;
        setMode("loading");
        setWorkspace(null);
        setDialog(false);
        setError(null);
        void (async () => {
            try {
                const provider = await officeEditingService.status({
                    fresh: true,
                });
                const existing =
                    props.docId != null
                        ? await speEditingService.status(props.docId)
                        : null;
                if (!alive) return;
                setWorkspace(existing);
                if (existing && existing.state !== "closed") {
                    setMode("spe");
                    setDialog(props.chooseTargetOnOpen === true);
                    return;
                }
                if (provider.enabled && provider.provider === "onlyoffice") {
                    setMode("onlyoffice");
                    setDialog(props.chooseTargetOnOpen === true);
                    return;
                }
                if (props.docId == null) {
                    setMode("readonly");
                    return;
                }
                setMode(
                    provider.enabled && provider.provider === "spe"
                        ? "spe"
                        : "disabled",
                );
                setDialog(props.chooseTargetOnOpen === true && provider.enabled && provider.provider === "spe");
            } catch {
                if (alive) {
                    setMode("disabled");
                    setError(
                        "Office-Status konnte nicht geladen werden. Bestehende Arbeitskopien bleiben erhalten. Bitte später erneut versuchen.",
                    );
                }
            }
        })();
        return () => {
            alive = false;
        };
    }, [props.docId, props.kennung, props.chooseTargetOnOpen]);
    if (mode === "onlyoffice" && (props.docId == null || onlyOfficeChosen))
        return <OnlyOfficeDocumentView {...props} />;
    const download = async () => {
        if (props.docId == null) return;
        try {
            const blob = await documentService.fetchBlob(props.docId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = props.fileName;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch {
            setError("Das Dokument konnte nicht heruntergeladen werden.");
        }
    };
    return (
        <div style={{ height: "100%", overflow: "auto", padding: 16 }}>
            <h2>{props.fileName}</h2>
            {mode === "loading" && <p>Office-Verfügbarkeit wird geprüft…</p>}
            {mode === "readonly" && (
                <p>
                    Leseansicht — die bestehende Datei bleibt über den Download
                    in der Ablage verfügbar.
                </p>
            )}
            {mode === "disabled" && (
                <p>
                    Office-Bearbeitung ist derzeit nicht verfügbar. Gespeicherte
                    Fassungen und Downloads bleiben erreichbar.
                </p>
            )}
            {error && <p role="alert">{error}</p>}
            {(mode === "spe" || mode === "onlyoffice") &&
                props.docId != null && (
                    <>
                        <Button onClick={() => setDialog(true)}>
                            Office-Dokument bearbeiten
                        </Button>
                        {workspace && (
                            <OfficeWorkspaceStatus
                                documentId={props.docId}
                                initialStatus={workspace}
                                onStatus={setWorkspace}
                            />
                        )}
                        {dialog && (
                            <OfficeOpenDialog
                                documentId={props.docId}
                                activeWorkspace={
                                    !!workspace && workspace.state !== "closed"
                                }
                                openOnlyOffice={(_id, tab) => {
                                    tab?.close();
                                    setOnlyOfficeChosen(true);
                                    setMode("onlyoffice");
                                }}
                                onDismiss={() => setDialog(false)}
                                onOpened={(status) => setWorkspace(status)}
                            />
                        )}
                    </>
                )}
            {props.docId != null && mode !== "loading" && (
                <>
                    <Button onClick={() => void download()}>
                        Herunterladen
                    </Button>
                    <Button onClick={() => setVersions(true)}>Fassungen</Button>
                    <DocumentVersionsDialog
                        docId={props.docId}
                        open={versions}
                        onOpenChange={setVersions}
                        onRestored={() => setWorkspace(null)}
                        canRestore={
                            workspace == null || workspace.state === "closed"
                        }
                    />
                </>
            )}
        </div>
    );
};
